/**
 * EU Disease Manager
 * 
 * Component for managing EU notifiable diseases (EU Decision 2018/945)
 * Part of Policy Framework & EU Diseases Implementation
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEUDiseases, useEUDiseaseMapping, useImmediateReportingDiseases, useAllEUDiseases } from '../../hooks/useEUDiseases';
import { useNotifications } from '../NotificationSystem';
import { biologicalEntityService } from '../../services/firebaseService';
import euDiseaseService from '../../services/euDiseaseService';
import Modal from '../ui/Modal';
import './EUDiseaseManager.css';

const EUDiseaseManager = ({ organizationId, userId, userName }) => {
  const { t } = useTranslation('ipc');
  const { euDiseases, statistics, loading, error, refresh } = useEUDiseases(organizationId);
  const { allDiseases, loading: allLoading, refresh: refreshAll } = useAllEUDiseases(organizationId);
  const { immediateDiseases } = useImmediateReportingDiseases(organizationId);
  const { runMapping, mappingResults, loading: mappingLoading } = useEUDiseaseMapping();
  const { notify } = useNotifications();

  const [showMappingModal, setShowMappingModal] = useState(false);
  const [showManualMapModal, setShowManualMapModal] = useState(false);
  const [selectedDisease, setSelectedDisease] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'mapped'
  const [availableEntities, setAvailableEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [createNewEntity, setCreateNewEntity] = useState(false);

  const handleRunMapping = async () => {
    try {
      const results = await runMapping(organizationId);
      
      if (results.matched && results.matched.length > 0) {
        notify.success(t('mappedEUDiseases', { count: results.matched.length }));
      }
      if (results.created && results.created.length > 0) {
        notify.success(t('createdEntitiesFromEU', { count: results.created.length }));
      }
      if (results.failed && results.failed.length > 0) {
        notify.warning(t('mappingsFailed', { count: results.failed.length }));
      }
      
      setShowMappingModal(false);
      refresh();
      refreshAll();
    } catch (err) {
      notify.error(err.message || t('failedToMapEUDiseases'));
    }
  };

  const handleViewDetails = (disease) => {
    // For unmapped diseases, use source data structure
    // For mapped diseases, use Firestore structure
    const diseaseData = disease.id 
      ? disease // Already mapped, use as-is
      : {
          // Unmapped disease from source - convert to display format
          id: disease.diseaseCode,
          commonName: disease.diseaseName,
          scientificName: disease.scientificName,
          euDiseaseCode: disease.diseaseCode,
          ecdcCode: disease.ecdcCode,
          euReportingDeadline: disease.reportingDeadline,
          euPublicHealthImportance: disease.publicHealthImportance,
          euIsolationRequired: disease.isolationRequired,
          euContactTracingRequired: disease.contactTracingRequired,
          euPreventionMeasures: disease.preventionMeasures || [],
          euRpnMultiplier: disease.rpnBaseMultiplier,
          euAlertThresholdMultiplier: disease.alertThresholdMultiplier,
          euOutbreakThreshold: disease.outbreakThreshold,
          euSeasonalPattern: disease.seasonalPattern,
          euSurveillanceSystem: disease.surveillanceSystem || 'TESSy',
          euCaseDefinition: disease.caseDefinition,
          euTransmissionMode: disease.transmissionMode || [],
          euIncubationPeriod: disease.incubationPeriod,
          // Keep original source data for mapping
          sourceDisease: disease
        };
    setSelectedDisease(diseaseData);
    setShowDetails(true);
  };

  const handleMapDisease = async (disease) => {
    // Load original source data if needed
    const sourceDisease = disease.sourceDisease || disease;
    
    setSelectedDisease({
      ...disease,
      sourceDisease: sourceDisease
    });
    
    // Load available biological entities
    setLoadingEntities(true);
    try {
      const entities = await biologicalEntityService.getWhere('organizationId', '==', organizationId);
      // Filter out already mapped entities (optional - you might want to show them)
      setAvailableEntities(entities);
    } catch (err) {
      notify.error('Failed to load biological entities: ' + err.message);
    } finally {
      setLoadingEntities(false);
    }
    
    setShowManualMapModal(true);
  };

  const handleManualMapping = async () => {
    if (!selectedDisease || !organizationId) return;
    
    try {
      const sourceDisease = selectedDisease.sourceDisease || selectedDisease;
      
      if (createNewEntity) {
        // Create new entity from disease
        const entityId = await euDiseaseService.createEntityFromEUDisease(
          organizationId,
          sourceDisease
        );
        notify.success(t('createdEntityForDisease', { name: sourceDisease.diseaseName }));
      } else if (selectedEntityId) {
        // Update existing entity with EU data
        await euDiseaseService.updateEntityWithEUData(selectedEntityId, sourceDisease);
        notify.success(`Mapped ${sourceDisease.diseaseName} to biological entity`);
      } else {
        notify.error('Please select an entity or choose to create a new one');
        return;
      }
      
      setShowManualMapModal(false);
      setSelectedEntityId('');
      setCreateNewEntity(false);
      refresh();
      refreshAll();
    } catch (err) {
      notify.error('Failed to map disease: ' + err.message);
    }
  };

  const handleUnmapDisease = async (disease) => {
    if (!disease.id || !organizationId) {
      notify.error(t('diseaseNotMappedToEntity'));
      return;
    }
    
    try {
      const { updateDoc, doc, Timestamp } = await import('firebase/firestore');
      const { db } = await import('../../firebase');
      const entityRef = doc(db, 'biological_entities', disease.id);
      
      await updateDoc(entityRef, {
        euNotifiable: false,
        euDiseaseCode: null,
        euDecision: null,
        euLastUpdated: Timestamp.now(),
        euUpdatedBy: userId || 'user',
        euUpdatedByName: userName || 'User'
      });
      
      notify.success(`Removed EU mapping from ${disease.commonName || disease.scientificName}`);
      refresh();
      refreshAll();
    } catch (err) {
      notify.error('Failed to unmap disease: ' + err.message);
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'CRITICAL': return '#ef4444';
      case 'HIGH': return '#f59e0b';
      case 'MEDIUM': return '#3b82f6';
      case 'LOW': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getReportingDeadlineBadge = (deadline) => {
    if (deadline === '24 hours') {
      return <span className="badge badge-urgent">24h</span>;
    }
    if (deadline === 'weekly') {
      return <span className="badge badge-warning">Weekly</span>;
    }
    return <span className="badge badge-info">{deadline}</span>;
  };

  if (loading) {
    return (
      <div className="eu-disease-manager">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('loadingEUNotifiableDiseases')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="eu-disease-manager">
      <div className="manager-header">
        <div>
          <h2>{t('euNotifiableDiseases')}</h2>
          <p className="subtitle">{t('euDecisionSubtitle')}</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => setShowMappingModal(true)}
            disabled={!organizationId || mappingLoading}
          >
            {mappingLoading ? t('mappingInProgress') : t('mapEUDiseases')}
          </button>
          {!organizationId && (
            <div className="error-message" style={{ marginTop: '16px' }}>
              ⚠️ {t('noOrganizationPolicy')}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <div className="statistics-grid">
          <div className="stat-card">
            <div className="stat-label">{t('totalEUDiseases')}</div>
            <div className="stat-value">{statistics.total || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">{t('reporting24h')}</div>
            <div className="stat-value">
              {statistics.byReportingDeadline?.['24 hours'] || 0}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">{t('requireIsolation')}</div>
            <div className="stat-value">{statistics.requireIsolation || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">{t('requireContactTracing')}</div>
            <div className="stat-value">{statistics.requireContactTracing || 0}</div>
          </div>
        </div>
      )}

      {/* Immediate Reporting Alert */}
      {immediateDiseases && immediateDiseases.length > 0 && (
        <div className="alert-section">
          <div className="alert alert-urgent">
            <strong>⚠️ {t('immediateReportingRequired')}</strong>
            <p>{t('diseasesRequire24hReporting', { count: immediateDiseases.length })}</p>
          </div>
        </div>
      )}

      {/* View Mode Toggle */}
      <div className="view-mode-toggle" style={{ marginBottom: '16px', padding: '12px', background: 'white', borderRadius: '8px', display: 'flex', gap: '12px', alignItems: 'center' }}>
        <label style={{ fontWeight: 500, color: '#4a5568' }}>{t('view')}:</label>
        <button
          className={`btn btn-sm ${viewMode === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setViewMode('all')}
        >
          {t('allEUDiseases')} ({allDiseases.length || 0})
        </button>
        <button
          className={`btn btn-sm ${viewMode === 'mapped' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setViewMode('mapped')}
        >
          {t('mappedOnly')} ({euDiseases.length || 0})
        </button>
      </div>

      {/* Diseases List */}
      <div className="diseases-list">
        {viewMode === 'all' ? (
          allLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>{t('loadingAllEUDiseases')}</p>
            </div>
          ) : allDiseases.length === 0 ? (
            <div className="empty-state">
              <p>{t('noEUDiseasesInSource')}</p>
            </div>
          ) : (
            <table className="diseases-table">
              <thead>
                <tr>
                  <th>{t('diseaseName')}</th>
                  <th>{t('scientificName')}</th>
                  <th>{t('euCode')}</th>
                  <th>{t('reportingDeadline')}</th>
                  <th>{t('importance')}</th>
                  <th>{t('mappingStatus')}</th>
                  <th>{t('rpnMultiplier')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {allDiseases.map(disease => (
                  <tr key={disease.diseaseCode} className={!disease.isMapped ? 'unmapped-disease' : ''}>
                    <td className="disease-name">
                      {disease.diseaseName}
                    </td>
                    <td className="scientific-name">{disease.scientificName}</td>
                    <td>
                      <span className="code-badge">{disease.diseaseCode}</span>
                    </td>
                    <td>
                      {getReportingDeadlineBadge(disease.reportingDeadline || 'N/A')}
                    </td>
                    <td>
                      <span
                        className="importance-badge"
                        style={{ backgroundColor: getImportanceColor(disease.publicHealthImportance) }}
                      >
                        {disease.publicHealthImportance || 'N/A'}
                      </span>
                    </td>
                    <td>
                      {disease.isMapped ? (
                        <span className="badge badge-success">{t('mapped')}</span>
                      ) : (
                        <span className="badge badge-warning">{t('notMapped')}</span>
                      )}
                    </td>
                    <td>
                      <span className="multiplier-badge">
                        {disease.rpnBaseMultiplier ? `×${disease.rpnBaseMultiplier}` : 'N/A'}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        className="btn btn-sm btn-view"
                        onClick={() => handleViewDetails(disease)}
                        style={{ marginRight: '8px' }}
                      >
                        {t('viewDetails')}
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleMapDisease(disease)}
                      >
                        {t('map')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : euDiseases.length === 0 ? (
          <div className="empty-state">
            <p>{t('noEUNotifiableDiseasesPrompt')}</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowMappingModal(true)}
              disabled={!organizationId}
            >
              {t('mapEUDiseases')}
            </button>
          </div>
        ) : (
          <table className="diseases-table">
            <thead>
              <tr>
                <th>{t('diseaseName')}</th>
                <th>{t('scientificName')}</th>
                <th>{t('euCode')}</th>
                <th>{t('reportingDeadline')}</th>
                <th>{t('importance')}</th>
                <th>{t('requireIsolation')}</th>
                <th>{t('rpnMultiplier')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {euDiseases.map(disease => (
                <tr key={disease.id}>
                  <td className="disease-name">
                    {disease.commonName || disease.scientificName}
                  </td>
                  <td className="scientific-name">{disease.scientificName}</td>
                  <td>
                    <span className="code-badge">{disease.euDiseaseCode || disease.ecdcCode}</span>
                  </td>
                  <td>
                    {getReportingDeadlineBadge(disease.euReportingDeadline)}
                  </td>
                  <td>
                    <span
                      className="importance-badge"
                      style={{ backgroundColor: getImportanceColor(disease.euPublicHealthImportance) }}
                    >
                      {disease.euPublicHealthImportance || 'N/A'}
                    </span>
                  </td>
                  <td>
                    {disease.euIsolationRequired ? (
                      <span className="badge badge-warning">{t('yes')}</span>
                    ) : (
                      <span className="badge badge-secondary">{t('no')}</span>
                    )}
                  </td>
                  <td>
                    <span className="multiplier-badge">
                      {disease.euRpnMultiplier ? `×${disease.euRpnMultiplier}` : 'N/A'}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      className="btn btn-sm btn-view"
                      onClick={() => handleViewDetails(disease)}
                      style={{ marginRight: '8px' }}
                    >
                      {t('viewDetails')}
                    </button>
                    {disease.id ? (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleUnmapDisease(disease)}
                        title={t('removeEUMappingTitle')}
                      >
                        {t('unmap')}
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleMapDisease(disease)}
                      >
                        Map
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mapping Modal */}
      <Modal
        isOpen={showMappingModal}
        onClose={() => setShowMappingModal(false)}
        title={t('mapEUDiseasesToEntities')}
        size="medium"
      >
        <div className="mapping-modal">
          <p>{t('mapEUDiseasesDescription')}</p>
          <div className="form-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowMappingModal(false)}
            >
              {t('cancel')}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleRunMapping}
              disabled={mappingLoading || !organizationId}
            >
              {mappingLoading ? t('mappingInProgress') : t('startMapping')}
            </button>
          </div>
          {mappingResults && (
            <div className="mapping-results">
              <h4>{t('mappingResults')}</h4>
              {mappingResults.matched && mappingResults.matched.length > 0 && (
                <p>✅ {t('matchedCount', { count: mappingResults.matched.length })}</p>
              )}
              {mappingResults.created && mappingResults.created.length > 0 && (
                <p>✅ {t('createdCount', { count: mappingResults.created.length })}</p>
              )}
              {mappingResults.failed && mappingResults.failed.length > 0 && (
                <p>❌ {t('failedCount', { count: mappingResults.failed.length })}</p>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Manual Mapping Modal */}
      <Modal
        isOpen={showManualMapModal}
        onClose={() => {
          setShowManualMapModal(false);
          setSelectedEntityId('');
          setCreateNewEntity(false);
        }}
        title={t('mapDiseaseTitle', { name: selectedDisease?.sourceDisease?.diseaseName || selectedDisease?.commonName || 'Disease' })}
        size="medium"
      >
        <div className="manual-mapping-modal">
          <div className="mapping-info">
            <p>
              <strong>{t('diseaseLabel')}:</strong> {selectedDisease?.sourceDisease?.diseaseName || selectedDisease?.commonName}
            </p>
            <p>
              <strong>{t('scientificName')}:</strong> {selectedDisease?.sourceDisease?.scientificName || selectedDisease?.scientificName}
            </p>
          </div>

          <div className="mapping-options">
            <label className="option-radio">
              <input
                type="radio"
                checked={!createNewEntity}
                onChange={() => setCreateNewEntity(false)}
              />
              <span>{t('mapToExistingEntity')}</span>
            </label>

            <label className="option-radio">
              <input
                type="radio"
                checked={createNewEntity}
                onChange={() => setCreateNewEntity(true)}
              />
              <span>{t('createNewEntity')}</span>
            </label>
          </div>

          {!createNewEntity && (
            <div className="entity-selector">
              <label>{t('selectBiologicalEntity')}</label>
              {loadingEntities ? (
                <div className="loading-state">{t('loadingEntities')}</div>
              ) : (
                <select
                  value={selectedEntityId}
                  onChange={(e) => setSelectedEntityId(e.target.value)}
                  className="entity-select"
                  style={{ width: '100%', padding: '8px', marginTop: '8px' }}
                >
                  <option value="">{t('selectAnEntity')}</option>
                  {availableEntities.map(entity => (
                    <option key={entity.id} value={entity.id}>
                      {entity.commonName || entity.scientificName} 
                      {entity.scientificName && entity.commonName ? ` (${entity.scientificName})` : ''}
                      {entity.euNotifiable && t('alreadyEUMapped')}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="form-actions" style={{ marginTop: '24px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowManualMapModal(false);
                setSelectedEntityId('');
                setCreateNewEntity(false);
              }}
            >
              {t('cancel')}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleManualMapping}
              disabled={(!createNewEntity && !selectedEntityId) || loadingEntities || !organizationId}
            >
              {createNewEntity ? t('createAndMap') : t('mapToEntity')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Disease Details Modal */}
      <Modal
        isOpen={showDetails}
        onClose={() => {
          setShowDetails(false);
          setSelectedDisease(null);
        }}
        title={selectedDisease ? (selectedDisease.commonName || selectedDisease.scientificName) : t('diseaseDetails')}
        size="large"
      >
        {selectedDisease && (
          <div className="disease-details">
            <div className="details-section">
              <h4>{t('identification')}</h4>
              <div className="detail-row">
                <label>{t('scientificName')}:</label>
                <span>{selectedDisease.scientificName}</span>
              </div>
              <div className="detail-row">
                <label>{t('commonNameLabel')}</label>
                <span>{selectedDisease.commonName || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <label>{t('euDiseaseCode')}:</label>
                <span>{selectedDisease.euDiseaseCode || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <label>{t('ecdcCode')}:</label>
                <span>{selectedDisease.ecdcCode || 'N/A'}</span>
              </div>
            </div>

            <div className="details-section">
              <h4>{t('reportingRequirements')}</h4>
              <div className="detail-row">
                <label>{t('reportingDeadline')}:</label>
                <span>{selectedDisease.euReportingDeadline || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <label>{t('surveillanceSystem')}:</label>
                <span>{selectedDisease.euSurveillanceSystem || 'TESSy'}</span>
              </div>
              <div className="detail-row">
                <label>{t('publicHealthImportance')}:</label>
                <span
                  className="importance-badge"
                  style={{ backgroundColor: getImportanceColor(selectedDisease.euPublicHealthImportance) }}
                >
                  {selectedDisease.euPublicHealthImportance || 'N/A'}
                </span>
              </div>
            </div>

            <div className="details-section">
              <h4>{t('controlMeasures')}</h4>
              <div className="detail-row">
                <label>{t('isolationRequired')}</label>
                <span>{selectedDisease.euIsolationRequired ? t('yes') : t('no')}</span>
              </div>
              <div className="detail-row">
                <label>{t('contactTracingRequired')}</label>
                <span>{selectedDisease.euContactTracingRequired ? t('yes') : t('no')}</span>
              </div>
              {selectedDisease.euPreventionMeasures && selectedDisease.euPreventionMeasures.length > 0 && (
                <div className="detail-row">
                  <label>{t('preventionMeasures')}</label>
                  <ul className="prevention-list">
                    {selectedDisease.euPreventionMeasures.map((measure, idx) => (
                      <li key={idx}>{measure}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="details-section">
              <h4>{t('riskAssessment')}</h4>
              <div className="detail-row">
                <label>{t('rpnMultiplier')}:</label>
                <span>{selectedDisease.euRpnMultiplier ? `×${selectedDisease.euRpnMultiplier}` : 'N/A'}</span>
              </div>
              <div className="detail-row">
                <label>{t('alertThresholdMultiplier')}</label>
                <span>{selectedDisease.euAlertThresholdMultiplier ? `×${selectedDisease.euAlertThresholdMultiplier}` : 'N/A'}</span>
              </div>
              <div className="detail-row">
                <label>{t('outbreakThreshold')}:</label>
                <span>{selectedDisease.euOutbreakThreshold || 'N/A'}</span>
              </div>
            </div>

            {selectedDisease.id && (
              <div className="details-section">
                <h4>{t('mappingStatus')}</h4>
                <div className="detail-row">
                  <label>{t('biologicalEntityId')}</label>
                  <span>{selectedDisease.id}</span>
                </div>
                <div className="detail-row">
                  <label>{t('statusLabel')}</label>
                  <span className="badge badge-success">{t('mapped')}</span>
                </div>
                <div className="form-actions" style={{ marginTop: '16px' }}>
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      handleUnmapDisease(selectedDisease);
                      setShowDetails(false);
                    }}
                  >
                    {t('unmapFromEntity')}
                  </button>
                </div>
              </div>
            )}

            {!selectedDisease.id && selectedDisease.sourceDisease && (
              <div className="details-section">
                <h4>{t('mappingStatus')}</h4>
                <div className="detail-row">
                  <label>{t('statusLabel')}</label>
                  <span className="badge badge-warning">{t('notMapped')}</span>
                </div>
                <div className="form-actions" style={{ marginTop: '16px' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowDetails(false);
                      handleMapDisease(selectedDisease);
                    }}
                  >
                    {t('mapToEntity')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EUDiseaseManager;

